// Documents Service for Attachments and Job Photos
import { supabaseAdmin as supabase } from '../utils/supabaseClient';
import { getSupabaseClient } from '../utils/supabaseClient';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/env';
import { compressImageForUpload } from '@tradesmatepro/shared-ui';

class DocumentsService {
  static getPathFromPublicUrl(publicUrl) {
    try {
      // Works for urls like: https://.../storage/v1/object/public/files/<path>
      const marker = '/storage/v1/object/public/';
      const idx = publicUrl.indexOf(marker);
      if (idx === -1) return null;
      const rest = publicUrl.substring(idx + marker.length); // files/<path>
      const parts = rest.split('/');
      const bucket = parts.shift();
      const path = parts.join('/');
      return { bucket, path };
    } catch { return null; }
  }

  // Helper: Insert record into documents table for tracking file size/limits
  static async trackDocumentUpload({
    companyId,
    workOrderId = null,
    customerId = null,
    documentType,
    title,
    fileName,
    fileUrl,
    fileSize,
    mimeType,
    uploadedBy
  }) {
    try {
      // Get authenticated user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      // Debug: Check if we have a valid session token
      if (!accessToken) {
        console.warn('⚠️ trackDocumentUpload: No session token available, skipping document tracking');
        return null;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('🔑 trackDocumentUpload: Using session token (first 20 chars):', accessToken?.substring(0, 20) + '...');
      }

      const documentData = {
        company_id: companyId,
        work_order_id: workOrderId || null,
        customer_id: customerId || null,
        document_type: documentType,
        title: title,
        file_name: fileName,
        file_url: fileUrl,
        file_size: fileSize,
        mime_type: mimeType,
        uploaded_by: uploadedBy,
        uploaded_at: new Date().toISOString()
      };

      console.log('📊 Tracking document in documents table:', documentData);

      const response = await fetch(`${SUPABASE_URL}/rest/v1/documents`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(documentData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('⚠️ Failed to track document (non-critical):', response.status, errorText);
        console.warn('⚠️ This usually means the "documents" table needs an INSERT RLS policy like: WITH CHECK (company_id = user_company_id())');
        return null;
      }

      const doc = await response.json();
      console.log('✅ Document tracked:', doc[0]?.id);
      return doc[0];
    } catch (error) {
      console.warn('⚠️ Document tracking failed (non-critical):', error.message);
      return null;
    }
  }
  // Upload attachment file
  static async uploadAttachment(companyId, jobId, file, uploadedBy, autoTags = [], ocrText = '') {
    try {
      // Ruling 4 (2026-11-02): photos are downscaled before upload; other files pass through.
      file = await compressImageForUpload(file);
      console.log('📎 DocumentsService.uploadAttachment called:', { companyId, jobId, fileName: file.name, fileSize: file.size });

      // Generate unique filename
      // #2221: split('.').pop() on a name with no dot returns the WHOLE name, which was then
      // stored as file_type and shown upper-cased as the Type. No extension -> no type (the
      // list shows "Unknown"), and no trailing dot on the storage name.
      const dot = file.name.lastIndexOf('.');
      const fileExt = dot > 0 ? file.name.slice(dot + 1) : '';
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const fileName = `${timestamp}-${randomId}${fileExt ? `.${fileExt}` : ''}`;

      // Use different path for general vs job-specific files
      const filePath = jobId
        ? `company-${companyId}/jobs/${jobId}/attachments/${fileName}`
        : `company-${companyId}/general/attachments/${fileName}`;

      console.log('📁 Upload path:', filePath);
      // Use 'company-files' bucket (public bucket with simpler policies)
      const BUCKET_NAME = 'company-files';
      console.log('🪣 Storage bucket:', BUCKET_NAME);

      // Upload to Supabase Storage
      console.log('📤 Starting storage upload...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Storage upload error:', JSON.stringify(uploadError, null, 2));
        throw new Error(`Storage upload failed: ${uploadError.message || uploadError.error || JSON.stringify(uploadError)}`);
      }

      console.log('✅ File uploaded to storage:', uploadData);

      // Do NOT store public URL for private buckets; store storage path instead
      const storagePath = filePath;
      console.log('🔗 Storage path to save (private):', storagePath);

      // Insert into attachments table
      const attachmentData = {
        work_order_id: jobId || null, // Allow null for general files
        company_id: companyId,
        uploaded_by: uploadedBy,
        file_url: storagePath, // store path; signed URL will be generated when reading
        file_name: file.name,
        file_type: fileExt ? fileExt.toLowerCase() : null,
        file_size: file.size,
        auto_tags: autoTags,
        ocr_text: ocrText,
        uploaded_at: new Date().toISOString()
      };

      console.log('💾 Saving metadata to attachments table:', attachmentData);

      // Get authenticated user's session token for RLS
      // On mobile, session might be stale - try to refresh first
      let session = null;
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.warn('⚠️ Session error, attempting refresh:', sessionError);
          const { data: refreshData } = await supabase.auth.refreshSession();
          session = refreshData?.session;
        } else {
          session = sessionData?.session;
        }
      } catch (authError) {
        console.error('❌ Auth error getting session:', authError);
      }

      const accessToken = session?.access_token;

      if (!accessToken) {
        console.error('❌ No access token available - user may not be authenticated');
        throw new Error('Not authenticated. Please log in again.');
      }

      const response = await fetch(`${SUPABASE_URL}/rest/v1/attachments`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(attachmentData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Database insert error:', response.status, errorText);
        throw new Error(`DB error: ${errorText.substring(0, 100)}`);
      }

      const attachment = await response.json();
      console.log('✅ Metadata saved:', attachment);

      // Track in documents table for file size/limit tracking
      await this.trackDocumentUpload({
        companyId,
        workOrderId: jobId,
        documentType: 'attachment',
        title: file.name,
        fileName: file.name,
        fileUrl: storagePath,
        fileSize: file.size,
        mimeType: file.type,
        uploadedBy
      });

      return {
        success: true,
        attachment: attachment[0],
        message: 'Attachment uploaded successfully!'
      };
    } catch (error) {
      console.error('❌ Error uploading attachment:', error);
      console.error('Error stack:', error.stack);
      return {
        success: false,
        message: `Upload failed: ${error.message}`
      };
    }
  }

  // The one definition of "is this an image", because attachments.file_type holds
  // TWO live conventions: bare extensions (what this service writes) and MIME
  // (what the portal wrote). Anything matching only one silently drops the other.
  static IMAGE_FILE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg', 'tif', 'tiff'];

  static isImageFileType(fileType) {
    if (!fileType) return false;
    const t = String(fileType).toLowerCase();
    return t.includes('image') || DocumentsService.IMAGE_FILE_TYPES.includes(t);
  }

  // Upload job photo
  static async uploadJobPhoto(companyId, workOrderId, file, uploadedBy, autoTags = []) {
    try {
      // Ruling 4 (2026-11-02): photos are downscaled before upload; other files pass through.
      file = await compressImageForUpload(file);
      console.log('📸 DocumentsService.uploadJobPhoto called:', { companyId, workOrderId, fileName: file.name, fileSize: file.size, autoTags });

      // Get authenticated user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token || SUPABASE_ANON_KEY;

      // Generate unique filename
      // #2221: split('.').pop() on a name with no dot returns the WHOLE name, which was then
      // stored as file_type and shown upper-cased as the Type. No extension -> no type (the
      // list shows "Unknown"), and no trailing dot on the storage name.
      const dot = file.name.lastIndexOf('.');
      const fileExt = dot > 0 ? file.name.slice(dot + 1) : '';
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const fileName = `${timestamp}-${randomId}${fileExt ? `.${fileExt}` : ''}`;

      // Use different path for general vs work order-specific photos
      const filePath = workOrderId
        ? `company-${companyId}/work-orders/${workOrderId}/photos/${fileName}`
        : `company-${companyId}/general/photos/${fileName}`;

      console.log('📁 Upload path:', filePath);
      // Use 'company-files' bucket (public bucket with simpler policies)
      const BUCKET_NAME = 'company-files';
      console.log('🪣 Storage bucket:', BUCKET_NAME);

      // Upload to Supabase Storage
      console.log('📤 Starting storage upload...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Storage upload error:', JSON.stringify(uploadError, null, 2));
        throw new Error(`Storage upload failed: ${uploadError.message || uploadError.error || JSON.stringify(uploadError)}`);
      }

      console.log('✅ File uploaded to storage:', uploadData);

      // Store the storage path (public URL can be constructed when reading)
      const storagePath = filePath;
      console.log('🔗 Storage path to save:', storagePath);

      // Photos are attachments (20260818b). job_photos had no file_name, file_size
      // or file_type column, so every value below except the tag used to be
      // collected here and thrown away one line later - that was PHOTO-META-1.
      const photoData = {
        company_id: companyId,
        work_order_id: workOrderId || null,
        file_url: storagePath, // store path; signed URL will be generated when reading
        file_name: file.name,
        file_type: fileExt ? fileExt.toLowerCase() : null, // extension, matching DocumentsService's other upload path
        file_size: file.size,
        // DURING is a job phase; a company file with no job gets no phase tag (#6073).
        auto_tags: autoTags.length ? autoTags : (workOrderId ? ['DURING'] : []),
        uploaded_by: uploadedBy,
        uploaded_at: new Date().toISOString()
      };

      console.log('💾 Saving metadata to attachments table:', photoData);

      const response = await fetch(`${SUPABASE_URL}/rest/v1/attachments`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(photoData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Database insert error:', response.status, errorText);
        throw new Error(`Failed to save photo metadata: ${errorText}`);
      }

      const photo = await response.json();
      console.log('✅ Metadata saved:', photo);

      // Track in documents table for file size/limit tracking
      await this.trackDocumentUpload({
        companyId,
        workOrderId,
        documentType: 'photo',
        title: `Job Photo - ${photoData.auto_tags[0]}`,
        fileName: file.name,
        fileUrl: storagePath,
        fileSize: file.size,
        mimeType: file.type,
        uploadedBy
      });

      return {
        success: true,
        photo: photo[0],
        message: 'Photo uploaded successfully!'
      };
    } catch (error) {
      console.error('❌ Error uploading photo:', error);
      console.error('Error stack:', error.stack);
      return {
        success: false,
        message: `Upload failed: ${error.message}`
      };
    }
  }

  // Get attachments for company with relations and extended search
  static async getAttachments(companyId, searchTerm = '', jobId = null) {
    try {
      // Get authenticated user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token || SUPABASE_ANON_KEY;

      let url = `${SUPABASE_URL}/rest/v1/attachments?company_id=eq.${companyId}&select=*,work_orders(id,title,customer_id)&order=uploaded_at.desc`;
      if (jobId) url += `&work_order_id=eq.${jobId}`;
      const response = await fetch(url, { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' } });
      if (response.ok) {
        let attachments = await response.json();
        
        // Fetch customers separately for each attachment's work order
        attachments = await Promise.all(
          (attachments || []).map(async (att) => {
            if (att.work_orders?.customer_id) {
              const customerResponse = await fetch(
                `${SUPABASE_URL}/rest/v1/customers?id=eq.${att.work_orders.customer_id}&select=name`,
                { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' } }
              );
              if (customerResponse.ok) {
                const customers = await customerResponse.json();
                if (customers.length > 0) {
                  att.work_orders.customers = customers[0];
                }
              }
            }
            return att;
          })
        );
        
        if (searchTerm) {
          const term = searchTerm.trim().toLowerCase();
          attachments = attachments.filter(att =>
            (att.file_url||'').toLowerCase().includes(term) ||
            (att.file_name||'').toLowerCase().includes(term) ||
            (att.file_type||'').toLowerCase().includes(term) ||
            (att.ocr_text||'').toLowerCase().includes(term) ||
            (att.auto_tags||[]).some(t => t.toLowerCase().includes(term)) ||
            att.work_orders?.title?.toLowerCase().includes(term) ||
            att.work_orders?.customers?.name?.toLowerCase().includes(term)
          );
        }
        return attachments;
      }
      return [];
    } catch (error) {
      console.error('Error getting attachments:', error);
      return [];
    }
  }

  // Get job photos for company
  static async getJobPhotos(companyId, searchTerm = '', workOrderId = null) {
    try {
      // Get authenticated user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token || SUPABASE_ANON_KEY;

      // Photos are attachments with an image file_type (20260818b). The predicate
      // covers BOTH live conventions: bare extensions, which is what this service
      // writes, and the MIME-style values on the rows the portal created. Matching
      // only one silently drops rows from the gallery.
      let url = `${SUPABASE_URL}/rest/v1/attachments?company_id=eq.${companyId}` +
        `&or=(file_type.in.(${DocumentsService.IMAGE_FILE_TYPES.join(',')}),file_type.like.image/*)` +
        `&select=*,work_orders(id,title)&order=uploaded_at.desc`;

      if (workOrderId) {
        url += `&work_order_id=eq.${workOrderId}`;
      }

      const response = await fetch(url, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        let photos = await response.json();

        // Filter by search term if provided
        if (searchTerm) {
          const term = searchTerm.trim().toLowerCase();
          photos = photos.filter(photo =>
            photo.file_name?.toLowerCase().includes(term) ||
            photo.auto_tags?.some(t => t?.toLowerCase().includes(term)) ||
            photo.work_orders?.title?.toLowerCase().includes(term) ||
            photo.work_orders?.id?.toLowerCase().includes(term)
          );
        }

        return photos;
      }

      return [];
    } catch (error) {
      console.error('Error getting job photos:', error);
      return [];
    }
  }

  // Update tags or metadata for attachment/photo
  static async updateTags(table, id, autoTags) {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
        method: 'PATCH',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
        body: JSON.stringify({ auto_tags: autoTags })
      });
      if (!response.ok) throw new Error('Failed to update tags');
      return await response.json();
    } catch (e) { console.error('updateTags failed', e); return null; }
  }

  // downloadName: when set, adds Content-Disposition (?download=<name>) so the browser saves the
  // file under its ORIGINAL name instead of the storage hash (cross-origin makes the anchor
  // `download` attribute unreliable, so we set it on the URL instead).
  // ----------------------------------------------------------------------------------------
  // Company files are PRIVATE (ruling 2026-10-14, board #2987, docs/PRODUCT_DECISIONS.md).
  //
  // From 2026-01-09 (3fca5a31c) until this change these helpers returned getPublicUrl links into
  // a public bucket: every company's documents, photos and receipts could be listed and
  // downloaded with no login. Staff now get SHORT-LIVED signed URLs made with their own session,
  // so storage RLS decides (owning-row rule). Customers never come through here — they use the
  // portal-files edge function.
  //
  // Stored values are a bare path ("company-<id>/…", bucket company-files) or a full storage URL
  // (older rows). company-assets (logos/banners) is public by design and keeps public URLs;
  // anything that is not one of our storage URLs (e.g. seed example.com links) passes through.
  // ----------------------------------------------------------------------------------------
  static PRIVATE_BUCKET = 'company-files';
  static MAX_SIGN_SECONDS = 3600;

  static parseStoredFile(stored) {
    if (!stored || typeof stored !== 'string') return null;
    if (/^https?:\/\//i.test(stored)) {
      if (!stored.includes('/storage/v1/object/')) return { external: stored };
      const m = stored.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/([^?]+)/);
      if (!m) return { external: stored };
      return { bucket: m[1], path: decodeURIComponent(m[2]) };
    }
    return { bucket: this.PRIVATE_BUCKET, path: stored.replace(/^\/+/, '') };
  }

  static async getSignedUrl(stored, expiresInSeconds = 3600, downloadName = null) {
    const parsed = this.parseStoredFile(stored);
    if (!parsed) return null;
    if (parsed.external) return parsed.external;
    try {
      if (parsed.bucket !== this.PRIVATE_BUCKET) {
        const { data } = supabase.storage.from(parsed.bucket)
          .getPublicUrl(parsed.path, downloadName ? { download: downloadName } : undefined);
        return data?.publicUrl || null;
      }
      const ttl = Math.min(Math.max(Number(expiresInSeconds) || 900, 60), this.MAX_SIGN_SECONDS);
      const { data, error } = await supabase.storage.from(parsed.bucket)
        .createSignedUrl(parsed.path, ttl, downloadName ? { download: downloadName } : undefined);
      if (error) {
        console.warn('Could not sign file URL:', error.message);
        return null;
      }
      return data?.signedUrl || null;
    } catch (e) {
      console.error('getSignedUrl failed:', e);
      return null;
    }
  }

  // Signed URLs already issued to THIS signed-in user, keyed by storage path. A new signature is a
  // new URL, and a new URL misses the browser cache — so re-signing on every mount re-downloaded
  // every photo on every view. Reusing the URL until it nears expiry lets the browser cache hit.
  // Same expiry, same signing call, same private bucket; the cache is per user and per tab.
  // OPT-IN ({ reuse: true }): a reused URL has less life left than a fresh one, so only callers
  // that re-check before it lapses (useSignedFileUrls) or that load the file at once and then reuse
  // the same URL (browser-cached, max-age 3600) may take one. Callers that fetch a stored URL much
  // later — e.g. a Documents preview clicked after the page sat open — keep signing fresh.
  static SIGNED_URL_REUSE_MIN_MS = 30 * 60 * 1000; // reuse only while 30+ minutes remain
  static _signedUrlCache = new Map(); // `${userId}|${ttl}|${path}` -> { url, expiresAt }

  /** Batch: { [storedValue]: url } for many stored values in one storage call. */
  static async getSignedUrls(storedValues, expiresInSeconds = 3600, { reuse = false } = {}) {
    const out = {};
    const privatePaths = [];
    const keyByPath = {};
    for (const stored of [...new Set((storedValues || []).filter(Boolean))]) {
      const parsed = this.parseStoredFile(stored);
      if (!parsed) continue;
      if (parsed.external) { out[stored] = parsed.external; continue; }
      if (parsed.bucket !== this.PRIVATE_BUCKET) {
        out[stored] = supabase.storage.from(parsed.bucket).getPublicUrl(parsed.path).data?.publicUrl || null;
        continue;
      }
      privatePaths.push(parsed.path);
      keyByPath[parsed.path] = stored;
    }
    if (privatePaths.length) {
      const ttl = Math.min(Math.max(Number(expiresInSeconds) || 900, 60), this.MAX_SIGN_SECONDS);
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id || 'anon';
      const now = Date.now();
      const cacheKey = (path) => `${userId}|${ttl}|${path}`;
      const toSign = [];
      for (const path of privatePaths) {
        const hit = this._signedUrlCache.get(cacheKey(path));
        if (reuse && hit && hit.expiresAt - now > Math.min(this.SIGNED_URL_REUSE_MIN_MS, (ttl * 1000) / 2)) {
          out[keyByPath[path]] = hit.url;
        } else {
          toSign.push(path);
        }
      }
      if (toSign.length) {
        const { data, error } = await supabase.storage.from(this.PRIVATE_BUCKET).createSignedUrls(toSign, ttl);
        if (error) console.warn('Could not sign file URLs:', error.message);
        (data || []).forEach((row, i) => {
          if (!row?.signedUrl) return;
          out[keyByPath[toSign[i]]] = row.signedUrl;
          this._signedUrlCache.set(cacheKey(toSign[i]), { url: row.signedUrl, expiresAt: now + ttl * 1000 });
        });
      }
    }
    return out;
  }

  // SYNCHRONOUS twin of getSignedUrl, for the places that need a URL as a value rather than a
  // promise — an `<img src>` cannot await.
  //
  // This body used to live inside getSignedUrl, which is declared `async` even though nothing in it
  // ever was (`getPublicUrl` is synchronous). That made it unusable from JSX, and the Documents
  // thumbnail therefore bound `doc.file_url` RAW into `<img src>` while every other consumer —
  // Download and Preview — normalised through the helper. `file_url` legitimately holds two shapes
  // (a full storage URL, or a bare path like "company-xxx/jobs/yyy/attachments/file.png", per the
  // comment below), and a bare path in an `src` is resolved by the browser against the APP origin:
  // https://www.tradesmatepro.com/company-…/photos/x.png, which 404s and renders a 0x0 broken image.
  //
  // Extracted rather than inlined at the call site so there stays exactly one place that knows how
  // to turn this column into a URL.
  static resolveStorageUrl(publicUrl, downloadName = null) {
    if (!publicUrl) return null;
    try {
      // Default to 'company-files' bucket (public bucket used for uploads)
      let bucket = 'company-files';
      let path = publicUrl;

      // If it's a full URL, parse it
      if (publicUrl.includes('/storage/v1/object/')) {
        const parsed = this.getPathFromPublicUrl(publicUrl);
        if (parsed) {
          bucket = parsed.bucket;
          path = parsed.path;
        }
      }
      // Otherwise, assume it's already a storage path (e.g., "company-xxx/jobs/yyy/attachments/file.png")

      // #2987: company-files is private — a synchronous public URL would be a dead link (and,
      // while the bucket was public, a leak). Await getSignedUrl / getSignedUrls instead.
      if (bucket === 'company-files') return null;
      const { data } = supabase.storage.from(bucket).getPublicUrl(path, downloadName ? { download: downloadName } : undefined);
      return data?.publicUrl || null;
    } catch (e) {
      console.error('resolveStorageUrl failed:', e);
      return null;
    }
  }

  // Delete attachment
  static async deleteAttachment(attachmentId, companyId) {
    try {
      // Get authenticated user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token || SUPABASE_ANON_KEY;

      // Get attachment details first
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/attachments?id=eq.${attachmentId}&company_id=eq.${companyId}`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Attachment not found');
      }

      const attachments = await response.json();
      if (attachments.length === 0) {
        throw new Error('Attachment not found');
      }

      const attachment = attachments[0];

      // Determine storage path and bucket from stored value.
      //
      // THE DEFAULT WAS 'files', A BUCKET THAT DOES NOT EXIST. Probed live 2026-09-11:
      // /storage/v1/object/public/files/… answers `NoSuchBucket`, while company-files
      // answers `NoSuchKey` — so the bucket, not just the object, was wrong. Nothing else
      // in the app names a 'files' bucket; every other path uses company-files. This
      // default applies to any row whose file_url is a bare storage path rather than a
      // full URL, which is 50 of the 54 attachment rows, so it was the COMMON case: the
      // removal below failed, the failure was swallowed as a warning, the row was deleted
      // anyway, and the file stayed in the bucket forever. A silent storage leak on almost
      // every delete.
      let bucketName = 'company-files';
      let filePath = attachment.file_url;
      if (typeof attachment.file_url === 'string' && attachment.file_url.startsWith('http')) {
        const parsed = DocumentsService.getPathFromPublicUrl(attachment.file_url);
        if (parsed) {
          bucketName = parsed.bucket || 'company-files';
          filePath = parsed.path;
        }
      }

      // DELETE THE ROW FIRST, THEN THE OBJECT. The order used to be the other way round,
      // and the two failure modes are not equally bad:
      //
      //   object first  -> if the row delete then fails, the file is already gone and the
      //                    row survives pointing at nothing. Storage answers that with a
      //                    JSON 400/NoSuchKey, and because JSON is not an image content
      //                    type the browser blocks it cross-origin (ERR_BLOCKED_BY_ORB),
      //                    so the Documents page renders a permanently broken thumbnail.
      //                    Three such rows exist in Jerry's company today.
      //   row first     -> if the object removal then fails, an unreferenced file is left
      //                    in the bucket. Invisible, costs a little storage, reclaimable.
      //
      // So the row is the authoritative record and goes first. Storage removal stays
      // non-fatal: by the time it runs the record is gone, and failing the whole call
      // would tell the user the delete failed when the thing they can see is already gone.
      const deleteResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/attachments?id=eq.${attachmentId}&company_id=eq.${companyId}`,
        {
          method: 'DELETE',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete attachment record');
      }

      const { error: storageError } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (storageError) {
        console.warn(`Attachment row ${attachmentId} was deleted, but removing ${bucketName}/${filePath} failed; the file is now unreferenced.`, storageError);
      }

      return { success: true, message: 'Attachment deleted successfully!' };
    } catch (error) {
      console.error('Error deleting attachment:', error);
      return { success: false, message: `Delete failed: ${error.message}` };
    }
  }

  // Delete job photo.
  // Photos are attachments now (20260818b), so this is deleteAttachment with the
  // old name kept for its three callers. The duplicated body it used to hold read
  // job_photos and photo_url, neither of which the page writes any more.
  static async deleteJobPhoto(photoId, companyId) {
    return DocumentsService.deleteAttachment(photoId, companyId);
  }

  // Validate file types
  static validateAttachment(file) {
    const allowedTypes = [
      // Microsoft Office Documents
      'doc', 'docx', 'dot', 'dotx', 'docm',           // Word
      'xls', 'xlsx', 'xlt', 'xltx', 'xlsm', 'xlsb',  // Excel
      'ppt', 'pptx', 'pot', 'potx', 'pptm', 'pps', 'ppsx', // PowerPoint
      'mdb', 'accdb',                                  // Access
      'pub',                                           // Publisher
      'vsd', 'vsdx',                                   // Visio
      'mpp',                                           // Project

      // Other Office Suites
      'odt', 'ods', 'odp', 'odg',                     // LibreOffice/OpenOffice
      'pages', 'numbers', 'key',                      // Apple iWork

      // Adobe Documents
      'pdf', 'ai', 'psd', 'indd',

      // Text files
      'txt', 'md', 'rtf', 'csv', 'tsv',

      // Images
      'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico',

      // Archives
      'zip', 'rar', '7z', 'tar', 'gz',

      // Other common business files
      'json', 'xml', 'html', 'css', 'js', 'yaml', 'yml'
    ];

    const fileExt = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(fileExt)) {
      return {
        valid: false,
        message: `File type ".${fileExt}" not allowed. Supported: Microsoft Office, PDF, Images, Archives, and more.`
      };
    }

    const maxSize = 50 * 1024 * 1024; // 50MB for beta testing
    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'File size too large. Maximum size is 50MB.'
      };
    }

    return { valid: true };
  }

  static validatePhoto(file) {
    const allowedTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico', 'heic', 'heif'];
    const fileExt = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(fileExt)) {
      return {
        valid: false,
        message: `Image type ".${fileExt}" not allowed. Supported types: ${allowedTypes.join(', ')}`
      };
    }

    const maxSize = 50 * 1024 * 1024; // 50MB for beta testing
    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'File size too large. Maximum size is 50MB.'
      };
    }

    return { valid: true };
  }

  // Format file size
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get file icon
  static getFileIcon(fileType) {
    switch (fileType?.toLowerCase()) {
      // Microsoft Word
      case 'doc': case 'docx': case 'dot': case 'dotx': case 'docm': return '📝';

      // Microsoft Excel
      case 'xls': case 'xlsx': case 'xlt': case 'xltx': case 'xlsm': case 'xlsb': return '📊';

      // Microsoft PowerPoint
      case 'ppt': case 'pptx': case 'pot': case 'potx': case 'pptm': case 'pps': case 'ppsx': return '📊';

      // Microsoft Access
      case 'mdb': case 'accdb': return '🗃️';

      // Microsoft Publisher
      case 'pub': return '📰';

      // Microsoft Visio
      case 'vsd': case 'vsdx': return '📐';

      // Microsoft Project
      case 'mpp': return '📅';

      // Other Office Suites
      case 'odt': case 'ods': case 'odp': case 'odg': return '📄';
      case 'pages': return '📝';
      case 'numbers': return '📊';
      case 'key': return '📊';

      // Adobe
      case 'pdf': return '📄';
      case 'ai': return '🎨';
      case 'psd': return '🖼️';
      case 'indd': return '📰';

      // Text files
      case 'txt': return '📄';
      case 'md': return '📝';
      case 'rtf': return '📝';
      case 'csv': case 'tsv': return '📊';

      // Images
      case 'png': case 'jpg': case 'jpeg': case 'gif': case 'bmp': case 'webp': case 'tiff': case 'tif': case 'ico': case 'heic': case 'heif': return '🖼️';
      case 'svg': return '🎨';

      // Archives
      case 'zip': case 'rar': case '7z': case 'tar': case 'gz': return '📦';

      // Code files
      case 'json': case 'xml': case 'html': case 'css': case 'js': case 'yaml': case 'yml': return '💻';

      default: return '📎';
    }
  }

  // Get photo type badge color
  static getPhotoTypeBadge(type) {
    switch (type?.toUpperCase()) {
      case 'BEFORE': return 'bg-red-100 text-red-800';
      case 'AFTER': return 'bg-green-100 text-green-800';
      case 'OTHER': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }
  static async uploadPackageHtml(companyId, html, fileName, uploadedBy = null) {
    try {
      const timestamp = Date.now();
      const name = fileName || `closeout-${timestamp}.html`;
      const filePath = `company-${companyId}/packages/${name}`;
      const blob = new Blob([html], { type: 'text/html' });
      // Use 'company-files' bucket (public bucket with simpler policies)
      const { error } = await supabase.storage.from('company-files').upload(filePath, blob, { upsert: false, contentType: 'text/html' });
      if (error) throw error;

      // Track in documents table for file size/limit tracking
      await this.trackDocumentUpload({
        companyId,
        documentType: 'closeout_package',
        title: name,
        fileName: name,
        fileUrl: filePath,
        fileSize: blob.size,
        mimeType: 'text/html',
        uploadedBy
      });

      // Return storage path; UI will generate a signed URL when needed
      return filePath;
    } catch (e) { console.error('uploadPackageHtml failed', e); return null; }
  }

  static async createAttachmentRecord(companyId, jobId, fileUrl, fileName, uploadedBy, autoTags = []) {
    try {
      const body = {
        company_id: companyId,
        work_order_id: jobId || null,
        file_url: fileUrl,
        file_name: fileName,
        file_type: 'html',
        file_size: (fileUrl?.length || 0),
        auto_tags: autoTags,
        uploaded_by: uploadedBy,
        uploaded_at: new Date().toISOString()
      };
      // Get authenticated user's session token for RLS
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token || SUPABASE_ANON_KEY;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/attachments`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Failed to create attachment record');
      const [row] = await res.json();
      return row;
    } catch (e) { console.error('createAttachmentRecord failed', e); return null; }
  }
}

export default DocumentsService;
